import * as React from 'react';
import {
    IonButton,
    IonChip,
    IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonText,
} from '@ionic/react';
import { copyOutline } from 'ionicons/icons';
import Page from '../../../components/core/Page';
import { Proforma, Customer } from '../../../models';
import { Ages } from '../../../utils/ages';
import { useToastStore } from '../../../redux/toast/facade';
import { statusColor, statusLabel } from './OrderCard/OrderCard';
import './order-detail-page.css';

interface Props {
    order: Proforma | null;
    isFetching: boolean;
    customer: Customer | null;
}

const PAYABLE_STATUSES: Proforma['status'][] = ['pending', 'failed'];

const isPayableStatus = (status: Proforma['status'] | undefined): boolean =>
    status !== undefined && PAYABLE_STATUSES.includes(status);

export const OrderDetailPage: React.FC<Props> = ({
    order,
    isFetching,
    customer,
}) => {
    const date = order?.createdAt ? new Ages(order.createdAt).toFormat('dd mmm yyyy') : '—';

    const showPaymentBlocks = order != null && isPayableStatus(order.status);

    const proformaMessage = order?.message?.trim() || '';
    const companyInstructions = customer?.company.shopPayLaterInstructions?.trim() || '';
    const showNotice = showPaymentBlocks && (proformaMessage !== '' || companyInstructions !== '');

    const bank = customer?.payableBankAccount;
    const showBanking = showPaymentBlocks && !!bank?.accountNo;
    const reference = order?.invoice?.numberText ?? order?.numberText ?? '';

    const showPayNow = showPaymentBlocks && customer?.company.shopPaymentProviderConfigured === true;
    const handlePayNow = () => {
        if (!order) return;
        const url = order.invoice?.paymentUrl || order.paymentUrl;
        if (url) {
            window.location.href = url;
        }
    };

    const { showSuccess, showError } = useToastStore();
    const handleCopy = async (label: string, value: string) => {
        if (!value) return;
        try {
            await navigator.clipboard.writeText(value);
            showSuccess({ message: `${label} copied`, duration: 1500 });
        } catch {
            showError({ message: `Could not copy ${label.toLowerCase()}`, duration: 2000 });
        }
    };

    return (
        <Page
            title={order ? order.numberText : 'Order'}
            showMenuButton={false}
            showBackButton={true}
            backRouterLink="/orders"
            showLoading={isFetching && !order}
        >
            {!order && !isFetching && (
                <div className="ion-padding">
                    <IonText color="medium">
                        <p>Order not found.</p>
                    </IonText>
                </div>
            )}

            {order && (
                <>
                    <div className="order-detail-page-header ion-padding">
                        <div className="order-detail-page-title-row">
                            <IonText>
                                <h2 className="order-detail-page-number">{order.numberText}</h2>
                            </IonText>
                            <IonChip color={statusColor(order.status)}>
                                {statusLabel(order.status)}
                            </IonChip>
                        </div>
                        <IonText color="medium">
                            <p className="order-detail-page-date">{date}</p>
                        </IonText>
                    </div>

                    <IonList lines="full" className="ion-no-margin">
                        <IonItem>
                            <IonLabel className="order-detail-page-col-header">
                                <strong>Description</strong>
                            </IonLabel>
                            <IonLabel slot="end" className="order-detail-page-col-header ion-text-right">
                                <strong>Qty</strong>
                            </IonLabel>
                            <IonLabel slot="end" className="order-detail-page-col-header ion-text-right order-detail-page-col-amount">
                                <strong>Amount</strong>
                            </IonLabel>
                        </IonItem>
                        {order.lines.map((line) => {
                            const isTicket = line.event != null;
                            return (
                                <IonItem
                                    key={line.id}
                                    button={isTicket}
                                    detail={isTicket}
                                    routerLink={isTicket ? `/events/${line.event!.uuid}` : undefined}
                                    routerDirection="forward"
                                >
                                    <IonLabel className="ion-text-wrap">{line.description}</IonLabel>
                                    <IonLabel slot="end" className="ion-text-right order-detail-page-col-qty">
                                        {line.quantity}
                                    </IonLabel>
                                    <IonLabel slot="end" className="ion-text-right order-detail-page-col-amount">
                                        {line.totalDisplay}
                                    </IonLabel>
                                </IonItem>
                            );
                        })}
                        <IonItem>
                            <IonLabel><strong>Total</strong></IonLabel>
                            <IonLabel slot="end" className="ion-text-right order-detail-page-col-amount">
                                <strong>{order.totalDisplay}</strong>
                            </IonLabel>
                        </IonItem>
                    </IonList>

                    {showNotice && (
                        <div className="ion-padding-horizontal ion-padding-top order-detail-page-notice">
                            <IonText>
                                <h3>Payment instructions</h3>
                                {proformaMessage !== '' && (
                                    <p className="order-detail-page-notice-text">{proformaMessage}</p>
                                )}
                                {companyInstructions !== '' && (
                                    <p className="order-detail-page-notice-text">{companyInstructions}</p>
                                )}
                            </IonText>
                        </div>
                    )}

                    {showBanking && (
                        <div className="ion-padding-horizontal ion-padding-top order-detail-page-banking-heading">
                            <IonText>
                                <h3>Pay by EFT</h3>
                            </IonText>
                            <IonList lines="full" className="ion-no-margin order-detail-page-banking">
                                {bank?.accountHolder && (
                                    <IonItem>
                                        <IonLabel>Account Name</IonLabel>
                                        <IonLabel slot="end" className="ion-text-right">{bank.accountHolder}</IonLabel>
                                    </IonItem>
                                )}
                                {bank?.name && (
                                    <IonItem>
                                        <IonLabel>Bank</IonLabel>
                                        <IonLabel slot="end" className="ion-text-right">{bank.name}</IonLabel>
                                    </IonItem>
                                )}
                                {bank?.branch ? (
                                    <IonItem
                                        button
                                        detail={false}
                                        onClick={() => handleCopy('Branch', bank.branch!)}
                                    >
                                        <IonLabel>Branch</IonLabel>
                                        <IonLabel slot="end" className="ion-text-right order-detail-page-copyable-value">
                                            {bank.branch}
                                        </IonLabel>
                                        <IonIcon
                                            slot="end"
                                            icon={copyOutline}
                                            aria-hidden="true"
                                            className="order-detail-page-copy-icon"
                                        />
                                    </IonItem>
                                ) : (
                                    <IonItem>
                                        <IonLabel>Branch</IonLabel>
                                        <IonLabel slot="end" className="ion-text-right">-</IonLabel>
                                    </IonItem>
                                )}
                                {bank?.accountNo && (
                                    <IonItem
                                        button
                                        detail={false}
                                        onClick={() => handleCopy('Account no', bank.accountNo!)}
                                    >
                                        <IonLabel>Account no</IonLabel>
                                        <IonLabel slot="end" className="ion-text-right order-detail-page-copyable-value">
                                            {bank.accountNo}
                                        </IonLabel>
                                        <IonIcon
                                            slot="end"
                                            icon={copyOutline}
                                            aria-hidden="true"
                                            className="order-detail-page-copy-icon"
                                        />
                                    </IonItem>
                                )}
                                {bank?.type && (
                                    <IonItem>
                                        <IonLabel>Type</IonLabel>
                                        <IonLabel slot="end" className="ion-text-right">{bank.type}</IonLabel>
                                    </IonItem>
                                )}
                                <IonItem
                                    button
                                    detail={false}
                                    onClick={() => handleCopy('Reference', reference)}
                                >
                                    <IonLabel>Reference</IonLabel>
                                    <IonLabel slot="end" className="ion-text-right order-detail-page-copyable-value">
                                        <strong>{reference}</strong>
                                    </IonLabel>
                                    <IonIcon
                                        slot="end"
                                        icon={copyOutline}
                                        aria-hidden="true"
                                        className="order-detail-page-copy-icon"
                                    />
                                </IonItem>
                            </IonList>
                        </div>
                    )}

                    {showPayNow && (
                        <div className="ion-padding order-detail-page-pay-now">
                            <IonButton
                                expand="block"
                                color="success"
                                onClick={handlePayNow}
                            >
                                Pay Now
                            </IonButton>
                        </div>
                    )}

                    {order?.invoice?.downloadUrl && (
                        <div className="ion-padding order-detail-page-download-invoice">
                            <IonButton
                                expand="block"
                                color="dark"
                                onClick={() => { window.location.href = order.invoice!.downloadUrl!; }}
                            >
                                Download Invoice
                            </IonButton>
                        </div>
                    )}
                </>
            )}
        </Page>
    );
};
